import AddIcon from '@mui/icons-material/Add';
import EventsIcon from '@mui/icons-material/Event';
import QueueIcon from '@mui/icons-material/Grading';
import OrderIcon from '@mui/icons-material/Inventory';
import TransactionsIcon from '@mui/icons-material/ListAlt';
import ShippingIcon from '@mui/icons-material/LocalShipping';
import PatientsIcon from '@mui/icons-material/People';
import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import CoaIcon from '@mui/icons-material/VerifiedOutlined';
import InventoryManagementIcon from '@mui/icons-material/Warehouse';
import { Box, Drawer, List, ListItemButton, ListItemIcon, Tooltip } from '@mui/material';
import { lighten, useTheme } from '@mui/material/styles';
import { RiShip2Fill } from 'react-icons/ri';

import { split } from 'lodash';
import PropTypes from 'prop-types';
import { forwardRef, useMemo } from 'react';
import { Link as RouterLink, useLocation } from 'react-router-dom';

import useRoles from 'common/hooks/useRoles';
import { EnableInventory } from 'common/utils';
import packageJson from '../../../package.json';
import KeyboardShortcuts from './KeyboardShortcuts';

const NavigationLink = ({ to, label, Icon }) => {
  const theme = useTheme();
  const { pathname } = useLocation();
  const match = split(pathname, '/')[1] === split(to, '/')[1];

  const renderLink = useMemo(
    () =>
      // eslint-disable-next-line react/no-unstable-nested-components
      forwardRef((itemProps, ref) => (
        // eslint-disable-next-line react/jsx-props-no-spreading
        <RouterLink to={to} ref={ref} {...itemProps} role={undefined} />
      )),
    [to]
  );

  return (
    <li>
      <Tooltip title={label} placement='right' arrow>
        <ListItemButton
          component={renderLink}
          sx={{
            bgcolor: match && `${lighten(theme.palette.primary.main, 0.85)} !important`,
            px: 1,
            display: 'flex',
            justifyContent: 'center',
          }}
        >
          <ListItemIcon
            sx={{
              color: match ? theme.palette.primary.main : 'white',
              minWidth: 0,
              '& > svg': { fontSize: 24 },
            }}
          >
            <Icon />
          </ListItemIcon>
        </ListItemButton>
      </Tooltip>
    </li>
  );
};

NavigationLink.propTypes = {
  to: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  Icon: PropTypes.elementType.isRequired,
};

const NavigationBar = () => {
  const theme = useTheme();
  const {
    viewEvents: hasEventsAccess,
    viewWorkQueue: hasWorkQueueAccess,
    viewAdminCenter: hasAdminCenterAccess,
    viewPaymentTransactions: hasTransactionsAccess,
    viewBatchShipments: hasBatchShipmentsAccess,
    viewShipments: hasShipmentsAccess,
    createOrder: hasCreateOrderAccess,
    viewPatientsList: hasPatientsListAccess,
    viewOrdersList: hasOrdersListAccess,
    viewInventoryModule: hasInventoryAccess,
    rxScanStation: hasRxScanAccess,
    viewCoa: hasCoaAccess,
  } = useRoles();

  return (
    <Drawer
      variant='permanent'
      PaperProps={{
        sx: {
          width: `calc(${theme.spacing(6)} + 1px)`,
          bgcolor: theme.palette.customTheme.navigationBar,
          overflow: 'hidden',
        },
        elevation: 4,
      }}
    >
      <nav
        aria-label='navigation list'
        style={{ flexGrow: 1, height: '100%', display: 'flex', flexDirection: 'column' }}
      >
        <List>
          {hasWorkQueueAccess && <NavigationLink to='/' label='Work Queue' Icon={QueueIcon} />}
          {hasPatientsListAccess && (
            <NavigationLink to='/patients' label='Patients' Icon={PatientsIcon} />
          )}
          {hasOrdersListAccess && <NavigationLink to='/orders' label='Orders' Icon={OrderIcon} />}
          {hasBatchShipmentsAccess && (
            <NavigationLink to='/batchShipments' label='Batch Shipments' Icon={ShippingIcon} />
          )}
          {hasShipmentsAccess && (
            <NavigationLink to='/shipments' label='Shipments' Icon={RiShip2Fill} />
          )}
          {hasEventsAccess && <NavigationLink to='/events' label='Events' Icon={EventsIcon} />}
          {hasTransactionsAccess && (
            <NavigationLink to='/transactions' label='Transactions' Icon={TransactionsIcon} />
          )}
          {EnableInventory && hasInventoryAccess && (
            <NavigationLink
              to='/inventory'
              label='Inventory Management'
              Icon={InventoryManagementIcon}
            />
          )}
          {hasAdminCenterAccess && (
            <NavigationLink to='/adminCenter' label='Admin Center' Icon={SettingsOutlinedIcon} />
          )}
          {hasCreateOrderAccess && (
            <NavigationLink to='/createOrder' label='Create New Order' Icon={AddIcon} />
          )}
          {hasRxScanAccess && !process.env.REACT_APP_DISABLE_RX_SCAN && (
            <NavigationLink to='/rx-scan' label='Rx Scan Station' Icon={QrCodeScannerIcon} />
          )}
        </List>
        {hasCoaAccess && (
          <List sx={{ mt: 'auto', pb: 0 }}>
            <NavigationLink to='/coa' label='Certificates of Analysis' Icon={CoaIcon} />
          </List>
        )}
      </nav>
      <KeyboardShortcuts />
      <Box
        sx={{
          textAlign: 'center',
          my: 1,
          color: 'white',
          fontSize: '0.75rem',
          fontWeight: 'bold',
          whiteSpace: 'nowrap',
        }}
      >
        v{packageJson.version}
      </Box>
    </Drawer>
  );
};

export default NavigationBar;
